<template>
    <div>
        <el-row class="mb-4">
            <el-col>
                <el-card>
                    <div class="legend-controls">
                        <div class="color-legend">
                            <strong>Block Time Legend:</strong>
                            <span class="legend-item"><span class="color-box green"></span> &lt; 375ms (Fast)</span>
                            <span class="legend-item"><span class="color-box yellow"></span> &lt; 425ms (Average)</span>
                            <span class="legend-item"><span class="color-box red"></span> &ge; 425ms (Slow)</span>
                        </div>
                        <div class="epoch-selector">
                            <label for="epoch-select"><strong>Epoch:</strong></label>
                            <el-select 
                                id="epoch-select"
                                v-model="selectedEpoch" 
                                @change="onEpochChange"
                                placeholder="Select Epoch"
                                style="width: 120px; margin-left: 10px;"
                            >
                                <el-option
                                    v-for="epoch in epochOptions"
                                    :key="epoch"
                                    :label="epoch"
                                    :value="epoch"
                                />
                            </el-select>
                        </div>
                    </div>
                </el-card>
            </el-col>
        </el-row>

        <el-row class="mb-4">
            <el-col>
                <el-card>
                    <template #header>
                        <div>Historic Block Time Performance</div>
                        <small>Network-wide block time trends from epoch 800 onwards</small>
                    </template>
                    <line-chart 
                        :data="historicChartData" 
                        :library="historicChartOptions" 
                        :colors="historicChartColors"
                        height="300px"
                    />
                </el-card>
            </el-col>
        </el-row>

        <el-row :gutter="20">
            <el-col :md="12">
                <el-card>
                    <template #header>
                        <div>Global Validator Performance Map</div>
                        <small>Median block time (ms) by validator location for epoch {{ selectedEpoch }}</small>
                    </template>
                    <div id="heatmap" style="height: 400px;"></div>
                </el-card>
            </el-col>
            <el-col :md="12">
                <el-card>
                    <template #header>
                        <div>Validator Block Time Distribution</div>
                        <small>Median block time vs. packed slots for epoch {{ selectedEpoch }}</small>
                    </template>
                    <canvas id="scatterChart" style="height: 400px;"></canvas>
                </el-card>
            </el-col>
        </el-row>

        <el-row class="mt-4" v-if="isCurrentEpoch">
            <el-col>
                <el-card>
                    <template #header>
                        <div>Recent Block Performance</div>
                        <small>Block time for the last 200 blocks (including skipped slots). Refreshes every 10 seconds.</small>
                    </template>
                    <column-chart 
                        :data="recentPerfChartData" 
                        :library="recentPerfChartOptions" 
                        height="300px" 
                        :key="chartKey"
                    />
                </el-card>
            </el-col>
        </el-row>

        <el-row class="mt-4" v-if="isCurrentEpoch">
            <el-col>
                <el-card>
                    <template #header>Live Block Times</template>
                    <el-table :data="liveRecentBlockData" stripe style="width: 100%" height="500">
                        <el-table-column prop="leader_name" label="Leader" min-width="150" fixed>
                            <template #default="scope">
                                <a :href="`/v/${validators[scope.row.leader] ? validators[scope.row.leader].vote_identity : scope.row.leader}`">
                                    {{ scope.row.leader_name }}
                                </a>
                                <span v-if="scope.row.leader_geo">
                                    ({{ scope.row.leader_geo }})
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="slot_time_ms" label="Slot Time (ms)" sortable>
                            <template #default="scope">
                                <span v-if="scope.row.is_skipped" class="text-muted">SKIPPED</span>
                                <strong v-else :style="{ color: getBlockTimeColor(scope.row.slot_time_ms) }">
                                    {{ scope.row.slot_time_ms.toFixed(2) }}
                                </strong>
                            </template>
                        </el-table-column>
                        <el-table-column v-for="endpoint in endpoints" :key="endpoint.id" :label="getEndpointLabel(endpoint)">
                            <template #default="scope">
                                <span v-if="scope.row.is_skipped" class="text-muted">-</span>
                                <span v-else :class="getDurationClass(scope.row.durations[endpoint.id], scope.row.slot_time_ms)">
                                    {{ formatDuration(scope.row.durations[endpoint.id], scope.row.slot_time_ms) }}
                                </span>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-col>
        </el-row>

        <el-row :gutter="20" class="mt-4">
            <el-col :md="12">
                <el-card>
                    <template #header>
                        <div>Fastest Leaders (Top 15) - Epoch {{ selectedEpoch }}</div>
                        <small>Filtered to leaders with ≥40 slots</small>
                    </template>
                    <leaderboard-table :leaders="fastestLeaders" :validators="validators" :cluster-performance="clusterPerformance" :get-block-time-color="getBlockTimeColor"></leaderboard-table>
                </el-card>
            </el-col>
            <el-col :md="12">
                <el-card>
                    <template #header>
                        <div>Slowest Leaders (Top 15) - Epoch {{ selectedEpoch }}</div>
                        <small>Filtered to leaders with ≥40 slots</small>
                    </template>
                    <leaderboard-table :leaders="slowestLeaders" :validators="validators" :cluster-performance="clusterPerformance" :get-block-time-color="getBlockTimeColor"></leaderboard-table>
                </el-card>
            </el-col>
        </el-row>

        <el-row class="mt-4">
            <el-col>
                <el-card>
                    <template #header>
                        <div>Software Version Performance - Epoch {{ selectedEpoch }}</div>
                        <small>Weighted average block time by software client and version (sorted by total slots).</small>
                    </template>
                    <column-chart 
                        :data="softwareChartData" 
                        :library="softwareChartOptions" 
                        height="400px"
                    />
                </el-card>
            </el-col>
        </el-row>

        <el-row class="mt-4">
            <el-col>
                <el-card class="credits-card">
                    <template #header>
                        <div>Credits & Data Sources</div>
                    </template>
                    <div class="credits-content">
                        <p><strong>Special thanks to:</strong></p>
                        <ul>
                            <li><a href="https://triton.one" target="_blank" rel="noopener">Triton.one</a> / <a href="https://validators.app" target="_blank" rel="noopener">Validators.app</a> for providing validator location data</li>
                            <li>The following validators who have graciously provided Firedancer websocket endpoints for our data collection:
                                <ul class="validator-credits">
                                    <li v-for="endpoint in endpoints" :key="endpoint.id">
                                        {{ getEndpointLabel(endpoint) }}
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <p class="text-muted"><small>This dashboard aggregates data from multiple sources to provide insights into Solana network performance. Block time measurements are collected from various Firedancer websocket  endpoints across multiple geos in the network, we then take the minimum figure for each slot and log before calculating a median per epoch, per validator to allow for a fair comparison..</small></p>
                    </div>
                </el-card>
            </el-col>
        </el-row>
    </div>
</template>

<script>
import { defineComponent, onMounted, ref, computed, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';

Chart.register(annotationPlugin);

// A simple leaderboard component to avoid repetition
const LeaderboardTable = defineComponent({
    name: 'LeaderboardTable',
    props: ['leaders', 'validators', 'clusterPerformance', 'getBlockTimeColor'],
    template: `
        <el-table :data="leaders" stripe>
            <el-table-column label="Leader" prop="leader" min-width="120">
                <template #default="scope">
                    <a :href="'/validators/' + (validators[scope.row.leader] ? validators[scope.row.leader].vote_identity : scope.row.leader)">
                        {{ getDisplayName(scope.row.leader) }}
                    </a>
                </template>
            </el-table-column>
            <el-table-column label="Median Block Time (ms)" prop="median_block_time" sortable>
                <template #default="scope">
                    <strong :style="{ color: getBlockTimeColor(scope.row.median_block_time) }">
                        {{ scope.row.median_block_time.toFixed(2) }}
                    </strong>
                    <small v-if="clusterPerformance && clusterPerformance.median_block_time > 0" :class="scope.row.median_block_time > clusterPerformance.median_block_time ? 'negative' : 'positive'">
                        ({{ ((scope.row.median_block_time - clusterPerformance.median_block_time) / clusterPerformance.median_block_time * 100).toFixed(1) }}%)
                    </small>
                </template>
            </el-table-column>
            <el-table-column label="Slots" prop="num_slots" sortable></el-table-column>
        </el-table>
    `,
    methods: {
        getDisplayName(leader) {
            const validator = this.validators[leader];
            if (validator && validator.name) {
                return validator.name;
            }
            if (validator && validator.vote_account) {
                // Truncate vote_account if it's a long public key
                const voteAccount = validator.vote_account;
                if (voteAccount && voteAccount.length > 8) {
                    return voteAccount.substring(0, 4) + '...' + voteAccount.substring(voteAccount.length - 4);
                }
                return voteAccount;
            }
            // Shorten account string to first 4 and last 4 characters
            if (leader && leader.length > 8) {
                return leader.substring(0, 4) + '...' + leader.substring(leader.length - 4);
            }
            return leader;
        }
    }
});


export default defineComponent({
    name: 'BlockTimeDashboard',
    components: {
        LeaderboardTable,
    },
    props: {
        mapMarkerData: Array,
        scatterData: Array,
        clusterPerformance: Object,
        fastestLeaders: Array,
        slowestLeaders: Array,
        softwarePerformance: Array,
        historicPerformance: Array,
        recentBlockData: Array,
        recentChartData: Object,
        endpoints: Array,
        validators: Object,
        currentEpoch: Object,
        selectedEpoch: Number,
        epochOptions: Array,
    },
    setup(props) {
        const liveRecentBlockData = ref(props.recentBlockData);
        const liveRecentChartData = ref(props.recentChartData);
        const chartKey = ref(0);
        const scatterChart = ref(null);
        const selectedEpoch = ref(props.selectedEpoch);

        const isCurrentEpoch = computed(() => {
            return selectedEpoch.value === props.currentEpoch.epoch;
        });

        const onEpochChange = (epoch) => {
            // Redirect to the same page with the new epoch parameter
            const url = new URL(window.location);
            url.searchParams.set('epoch', epoch);
            window.location.href = url.toString();
        };

        const fetchLiveBlockData = async () => {
            if (!isCurrentEpoch.value) return;
            
            try {
                const response = await fetch('/statistics/block-times/live');
                if (response.ok) {
                    liveRecentBlockData.value = await response.json();
                } else {
                    console.error('Failed to fetch live block data');
                }
            } catch (error) {
                console.error('Error fetching live block data:', error);
            }
        };

        const fetchRecentChartData = async () => {
            if (!isCurrentEpoch.value) return;
            
            try {
                const response = await fetch('/statistics/block-times/recent-chart');
                if (response.ok) {
                    liveRecentChartData.value = await response.json();
                    chartKey.value += 1; // Force re-render
                } else {
                    console.error('Failed to fetch recent chart data');
                }
            } catch (error) {
                console.error('Error fetching recent chart data:', error);
            }
        };

        const getBlockTimeColor = (timeInMs) => {
            if (timeInMs === null || timeInMs === undefined) return '#909399'; // grey for no data
            if (timeInMs < 375) return '#67c23a'; // green
            if (timeInMs < 425) return '#e6a23c'; // yellow
            return '#f56c6c'; // red
        };

        const getSpeedClass = (avgBlockTime) => {
            if (avgBlockTime < 375) return 'fast';
            if (avgBlockTime < 425) return 'moderate';
            return 'slow';
        };

        const getEndpointLabel = (endpoint) => {
            let label = endpoint.name || endpoint.url;
            
            // If we have an account, look up validator info
            if (endpoint.account && props.validators[endpoint.account]) {
                const validator = props.validators[endpoint.account];
                label = validator.name || endpoint.account.substring(0, 8);
                
                // Add location if available
                if (validator.data_center_key) {
                    const parts = validator.data_center_key.split('-');
                    if (parts.length > 2) { // e.g., 24940-F-Helsinki
                        label += ` (${parts[2]}, ${parts[1].toUpperCase()})`;
                    } else if (parts.length > 1 && parts[1].length === 2) {
                        label += ` (${parts[1].toUpperCase()})`;
                    }
                }
            } else if (endpoint.location) {
                label += ` (${endpoint.location})`;
            }
            
            return label;
        };

        const initializeScatterChart = () => {
            if (!props.scatterData || props.scatterData.length === 0) return;

            const ctx = document.getElementById('scatterChart').getContext('2d');
            
            // Prepare data for Chart.js scatter chart
            const chartData = props.scatterData.map(point => ({
                x: point.x,
                y: point.y,
                label: point.label
            }));

            const xValues = props.scatterData.map(p => p.x);
            const xMin = Math.min(...xValues);
            const xMax = Math.max(...xValues);

            const config = {
                type: 'scatter',
                data: {
                    datasets: [{
                        label: 'Validators',
                        data: chartData,
                        backgroundColor: props.scatterData.map(p => getBlockTimeColor(p.x)),
                        borderColor: '#ffffff',
                        borderWidth: 1,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false
                        },
                        tooltip: {
                            callbacks: {
                                label: function(context) {
                                    const point = context.raw;
                                    return `${point.label}: (${point.x.toFixed(2)}ms, ${point.y} slots)`;
                                }
                            }
                        },
                        annotation: {
                            annotations: {
                                line1: {
                                    type: 'line',
                                    xMin: props.clusterPerformance?.median_block_time || 400,
                                    xMax: props.clusterPerformance?.median_block_time || 400,
                                    borderColor: 'rgb(255, 99, 132)',
                                    borderWidth: 2,
                                    label: {
                                        content: `Cluster Median: ${props.clusterPerformance?.median_block_time?.toFixed(2) || 'N/A'}ms`,
                                        enabled: true,
                                        position: 'start',
                                        backgroundColor: 'rgba(255, 99, 132, 0.8)',
                                        color: 'white',
                                        padding: 4
                                    }
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            title: {
                                display: true,
                                text: 'Median Block Time (ms)'
                            },
                            min: Math.max(0, xMin * 0.9),
                            max: xMax * 1.1
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'Packed Slots'
                            }
                        }
                    }
                }
            };

            scatterChart.value = new Chart(ctx, config);
        };

        onMounted(() => {
            const map = L.map('heatmap').setView([20, 0], 2);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(map);

            if (props.mapMarkerData && props.mapMarkerData.length > 0) {
                const markers = L.markerClusterGroup({
                    iconCreateFunction: function (cluster) {
                        const children = cluster.getAllChildMarkers();
                        const count = children.length;
                        let totalWeightedTime = 0;
                        let totalSlots = 0;
                        
                        children.forEach(c => {
                            const data = c.options.customData;
                            // Use weighted average: sum(time * slots) / total_slots
                            totalWeightedTime += data.median_block_time * (data.num_slots || 1);
                            totalSlots += (data.num_slots || 1);
                        });
                        
                        const avgBlockTime = totalSlots > 0 ? totalWeightedTime / totalSlots : 0;

                        // Determine size class based on count
                        let sizeClass = 'marker-cluster-small';
                        if (count >= 10) sizeClass = 'marker-cluster-medium';
                        if (count >= 100) sizeClass = 'marker-cluster-large';

                        // Determine speed class based on average block time
                        const speedClass = getSpeedClass(avgBlockTime);

                        // Create custom className that combines size and speed
                        const className = `marker-cluster ${sizeClass} cluster-${speedClass}`;

                        return L.divIcon({
                            html: `<div>
                                <span class="cluster-count">${count}</span>
                                <span class="cluster-speed">${avgBlockTime.toFixed(0)}ms</span>
                            </div>`,
                            className: className,
                            iconSize: new L.Point(40, 40)
                        });
                    },
                    // Disable default styling options
                    spiderfyOnMaxZoom: true,
                    showCoverageOnHover: false,
                    zoomToBoundsOnClick: true,
                    maxClusterRadius: 80,
                    // Disable animations to prevent style conflicts
                    animateAddingMarkers: false
                });

                props.mapMarkerData.forEach(data => {
                    const marker = L.circleMarker([data.lat, data.lng], {
                        radius: 5,
                        fillColor: getBlockTimeColor(data.median_block_time),
                        color: '#000',
                        weight: 1,
                        opacity: 1,
                        fillOpacity: 0.8,
                        customData: data // Store original data on marker
                    }).bindPopup(`<b>${data.name}</b><br>Median Block Time: ${data.median_block_time.toFixed(2)}ms`);
                    markers.addLayer(marker);
                });
                map.addLayer(markers);
            }

            // Initialize scatter chart after DOM is ready
            nextTick(() => {
                initializeScatterChart();
            });

            // Only start live data fetching for current epoch
            if (isCurrentEpoch.value) {
                setInterval(fetchLiveBlockData, 10000);
                setInterval(fetchRecentChartData, 10000);
            }
        });

        const historicChartData = computed(() => {
            if (!props.historicPerformance || props.historicPerformance.length === 0) {
                return [];
            }
            
            return [
                {
                    name: 'Network Median',
                    data: props.historicPerformance.reduce((acc, item) => {
                        acc[item.epoch] = item.cluster_median;
                        return acc;
                    }, {})
                },
                {
                    name: 'Best Validator',
                    data: props.historicPerformance.reduce((acc, item) => {
                        acc[item.epoch] = item.min_block_time;
                        return acc;
                    }, {})
                },
                {
                    name: 'Worst Validator',
                    data: props.historicPerformance.reduce((acc, item) => {
                        acc[item.epoch] = item.max_block_time;
                        return acc;
                    }, {})
                }
            ];
        });

        const historicChartColors = computed(() => {
            return ['#409eff', '#67c23a', '#f56c6c']; // blue, green, red
        });

        const historicChartOptions = computed(() => {
            return {
                plugins: {
                    legend: { 
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        callbacks: {
                            title: function(context) {
                                return `Epoch: ${context[0].label}`;
                            },
                            label: function(context) {
                                return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}ms`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Epoch'
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Block Time (ms)'
                        }
                    }
                }
            };
        });

        const softwareChartData = computed(() => {
            if (!props.softwarePerformance || props.softwarePerformance.length === 0) {
                return {};
            }
            
            const chartData = {};
            props.softwarePerformance.forEach((item) => {
                chartData[item.label] = item.value;
            });
            
            return chartData;
        });

        const softwareChartOptions = computed(() => {
            return {
                plugins: {
                    legend: { 
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                const item = props.softwarePerformance.find(p => p.label === context.label);
                                if (item) {
                                    return [
                                        `Average Block Time: ${context.parsed.y.toFixed(2)}ms`,
                                        `Total Slots: ${item.total_slots.toLocaleString()}`,
                                        `Validators: ${item.validator_count}`
                                    ];
                                }
                                return context.parsed.y.toFixed(2) + 'ms';
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            maxRotation: 45,
                            minRotation: 45
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Average Block Time (ms)'
                        }
                    }
                }
            };
        });

        const recentPerfChartData = computed(() => {
            if (!liveRecentChartData.value || !liveRecentChartData.value.blocks) {
                return {};
            }
            
            // Create a single series with slot numbers as keys and block times as values
            const chartData = {};
            liveRecentChartData.value.blocks.forEach((block) => {
                const slotKey = `${block.slot}`;
                const value = block.is_skipped ? 0 : block.duration_ms;
                chartData[slotKey] = value;
            });
            
            return chartData;
        });

        const recentPerfChartOptions = computed(() => {
            const blocks = liveRecentChartData.value?.blocks || [];

            return {
                plugins: {
                    legend: { 
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            title: function(context) {
                                const slotNumber = context[0].label;
                                return `Slot: ${slotNumber}`;
                            },
                            label: function(context) {
                                const slotNumber = context.label;
                                const block = blocks.find(b => b.slot.toString() === slotNumber);
                                if (block) {
                                    if (block.is_skipped) {
                                        return `${block.leader_name}: SKIPPED`;
                                    }
                                    return `${block.leader_name}: ${block.duration_ms.toFixed(2)}ms`;
                                }
                                return context.parsed.y + 'ms';
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            maxRotation: 90,
                            minRotation: 90,
                            callback: function(value, index, values) {
                                // show every 10th label to avoid clutter
                                return index % 10 === 0 ? blocks[index]?.slot : '';
                            }
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Block Time (ms)'
                        }
                    }
                }
            };
        });

        const formatDuration = (duration, minDurationMs) => {
            if (duration === null || duration === undefined) return 'N/A';
            const durationMs = duration / 1_000_000;
            const diff = durationMs - minDurationMs;
            if (Math.abs(diff) < 1) return '✓';
            return `+${diff.toFixed(0)}ms`;
        };

        const getDurationClass = (duration, minDurationMs) => {
            return 'text-muted';
        };

        return {
            formatDuration,
            getDurationClass,
            liveRecentBlockData,
            liveRecentChartData,
            recentPerfChartData,
            recentPerfChartOptions,
            softwareChartData,
            softwareChartOptions,
            historicChartData,
            historicChartOptions,
            historicChartColors,
            getEndpointLabel,
            chartKey,
            getBlockTimeColor,
            getSpeedClass,
            selectedEpoch,
            isCurrentEpoch,
            onEpochChange,
        };
    }
});
</script>
